import { colors } from '@lmc/common/constants'
import { Text } from '@lmc/ui-kit/src/general'
import { Gallery } from 'components/Gallery'
import { Icon } from 'components/generic/Icon'
import { NAMESPACE } from 'locale'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { StockRecord } from '../../types'
import { FileGalleryItem } from './FileGalleryItem'
import { ImageGalleryItem } from './ImageGalleryItem'

type AdditionalDetailsSectionProps = {
  record: StockRecord
}

export const AdditionalDetailsSection = ({
  record
}: AdditionalDetailsSectionProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const mediaAttachments = record.mediaAttachments || []
  const hasMedia = mediaAttachments.length > 0

  if (!hasMedia) {
    return null
  }

  const isVideo = (url: string, contentType?: string) => {
    if (contentType?.startsWith('video/')) return true
    return /\.(mp4|webm|ogg|mov)/i.test(url)
  }

  const isImage = (url: string, contentType?: string) => {
    if (contentType?.startsWith('image/')) return true
    return /\.(jpg|jpeg|png|gif|webp)/i.test(url)
  }

  return (
    <AttachmentsSection>
      <StyledLabel level={2}>{t('ATTACHMENTS')}</StyledLabel>
      <AttachmentsContainer>
        <Gallery>
          {mediaAttachments.map((attachment, index) => {
            const url = attachment.url
            const contentType = attachment.contentType
            const fileName = attachment.key?.split('/').pop()

            if (isVideo(url, contentType)) {
              return (
                <VideoThumbnailComponent
                  key={attachment._id || index}
                  videoUrl={url}
                />
              )
            }

            if (isImage(url, contentType)) {
              return (
                <ImageGalleryItem
                  key={attachment._id || index}
                  src={url}
                  alt={`Image attachment ${index + 1}`}
                />
              )
            }

            return (
              <FileGalleryItem
                key={attachment._id || index}
                url={url}
                fileName={fileName}
              />
            )
          })}
        </Gallery>
      </AttachmentsContainer>
    </AttachmentsSection>
  )
}

const AttachmentsSection = styled.div`
  padding: 16px 16px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const AttachmentsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const VideoThumbnailWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`

const PlayIconOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
`

const VideoThumbnailComponent = ({ videoUrl }: { videoUrl: string }) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false)

  const handleThumbnailClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsVideoOpen(true)
  }

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsVideoOpen(false)
  }

  return (
    <>
      <VideoThumbnailWrapper onClick={handleThumbnailClick}>
        <VideoThumbnailVideo
          src={videoUrl}
          muted
          onLoadedData={(e) => {
            const video = e.target as HTMLVideoElement
            video.currentTime = 0.1
          }}
        />
        <PlayIconOverlay>
          <Icon name="play" fill={colors.white} size={24} />
        </PlayIconOverlay>
      </VideoThumbnailWrapper>

      {isVideoOpen && (
        <VideoPopupBox onClick={handleClose}>
          <VideoPopupContent onClick={(e) => e.stopPropagation()}>
            <VideoCloseIcon onClick={handleClose}>×</VideoCloseIcon>
            <VideoPlayer controls autoPlay>
              <source src={videoUrl} type="video/mp4" />
            </VideoPlayer>
          </VideoPopupContent>
        </VideoPopupBox>
      )}
    </>
  )
}

const VideoThumbnailVideo = styled.video`
  width: 72px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  pointer-events: none;
`

const VideoPopupBox = styled.div`
  position: fixed;
  background: #00000050;
  width: 100%;
  height: 100vh;
  top: 0;
  left: 0;
  z-index: 1000;
`

const VideoPopupContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70%;
  margin: 0 auto;
  max-height: 70vh;
  margin-top: calc(100vh - 85vh - 20px);
  background: ${colors.white};
  border-radius: 10px;
  padding: 20px;
  overflow: auto;
`

const VideoCloseIcon = styled.span`
  cursor: pointer;
  position: fixed;
  right: calc(15% - 30px);
  top: calc(100vh - 85vh - 33px);
  background: ${colors.white};
  width: 25px;
  height: 25px;
  border-radius: 50%;
  line-height: 20px;
  text-align: center;
  font-size: 20px;
  z-index: 1001;
`

const VideoPlayer = styled.video`
  width: 100%;
  max-height: 60vh;
  border-radius: 10px;
  background: ${colors.background};
`
